import { useEffect, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { learningDevelopmentApi } from '../../learning-development/learningDevelopmentApi';
import type {
  LearningDevelopmentCatalogSlug,
  LearningDevelopmentOption,
} from '../../learning-development/learningDevelopmentTypes';
import { personnelApi } from '../personnelApi';
import type { PersonnelTraining } from '../personnelTypes';
import { sampleTraining } from '../utils/sampleTraining';

/** The four vocabularies this form picks from, loaded together. */
type Vocabularies = Record<LearningDevelopmentCatalogSlug, LearningDevelopmentOption[]>;

const EMPTY: Vocabularies = { types: [], categories: [], interventions: [], sponsors: [] };

/**
 * Records or corrects one L&D record — CS Form 212, section VII.
 *
 * **Four catalog pickers, and every one of them can add a row in place.** The type, the
 * category, the intervention and the sponsor are vocabularies maintained under Personnel →
 * Settings, and a clerk genuinely meets a novel value mid-form — a sponsor most of all, since
 * that list starts empty and is this employer's own. Adding one is gated on
 * `learning-development.create` and hidden without it, and the create is idempotent on the name
 * so two readers adding the same value get one row.
 *
 * **The competency-development block is WITHHELD.** Product call, 15 Sep 2026: purpose, the
 * competencies addressed, and the two levels are no longer asked for, are not required, and
 * default to null. The columns and the endpoint keep them, so restoring the block is a change
 * to this file rather than a migration — see `PersonnelTraining`.
 *
 * **No record number field.** It is assigned by the server and permanent, so the form neither
 * shows it on a create nor lets it be typed on an edit; the panel's own column is where it is
 * read. Same contract as the employee number.
 */
export function EditTrainingModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelTraining | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const canAddVocabulary = usePermission('learning-development.create');

  const [vocabularies, setVocabularies] = useState<Vocabularies>(EMPTY);
  const [loadingVocabularies, setLoadingVocabularies] = useState(true);

  const [title, setTitle] = useState(editing?.title ?? '');
  const [typeId, setTypeId] = useState<string | null>(editing?.typeId ?? null);
  const [categoryId, setCategoryId] = useState<string | null>(editing?.categoryId ?? null);
  const [interventionId, setInterventionId] = useState<string | null>(
    editing?.interventionId ?? null,
  );
  const [sponsorId, setSponsorId] = useState<string | null>(editing?.sponsorId ?? null);
  const [dateFrom, setDateFrom] = useState(editing?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(editing?.dateTo ?? '');
  const [numberOfHours, setNumberOfHours] = useState(
    editing?.numberOfHours === null || editing?.numberOfHours === undefined
      ? ''
      : String(editing.numberOfHours),
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    /*
      All four in one pass. They are four requests because they are four resources, but they are
      one wait as far as the reader is concerned — loading them in sequence would make the form
      usable a field at a time, which reads as it being broken.
    */
    Promise.all([
      learningDevelopmentApi.options('types', controller.signal),
      learningDevelopmentApi.options('categories', controller.signal),
      learningDevelopmentApi.options('interventions', controller.signal),
      learningDevelopmentApi.options('sponsors', controller.signal),
    ])
      .then(([types, categories, interventions, sponsors]) => {
        if (!controller.signal.aborted) {
          setVocabularies({ types, categories, interventions, sponsors });
        }
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('Could not load the L&D vocabularies.', 0),
        );
        // No focus call: a failed picker load names no field.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingVocabularies(false);
      });

    return () => controller.abort();
  }, []);

  const today = todayCalendarDate();

  const hoursValue = numberOfHours.trim() === '' ? null : Number(numberOfHours);
  const hoursIsNumber = hoursValue === null || Number.isInteger(hoursValue);
  const hoursNotNegative = hoursValue === null || hoursValue >= 0;

  const endsBeforeStart = dateTo !== '' && dateFrom !== '' && dateTo < dateFrom;

  const valid =
    title.trim().length > 0 &&
    typeId !== null &&
    categoryId !== null &&
    hoursIsNumber &&
    hoursNotNegative &&
    !endsBeforeStart;

  const canSave = valid && confirmed && !saving;

  /**
   * Adds a vocabulary row from inside its picker, and selects it.
   *
   * The pickers are over FOREIGN KEYS, so committing the typed text — what a bare `allowCustom`
   * does — would put a label where an id belongs. Writing the catalog row first and selecting
   * the id it comes back with is the only version that saves.
   */
  function addTo(catalog: LearningDevelopmentCatalogSlug, select: (id: string) => void) {
    return async (raw: string) => {
      const name = raw.trim();
      if (!name) return;

      try {
        const created = await learningDevelopmentApi.create(catalog, { name, description: null });

        setVocabularies((current) => ({
          ...current,
          // Idempotent on the name, so a value that already existed comes back rather than
          // being added twice — the list must not grow a duplicate either.
          [catalog]: current[catalog].some((o) => o.id === created.id)
            ? current[catalog]
            : [...current[catalog], { id: created.id, name: created.name }],
        }));

        select(created.id);
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not add that value.', 0),
        );
      }
    };
  }

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleTraining(vocabularies);

    setTitle(sample.title);
    if (sample.typeId) setTypeId(sample.typeId);
    if (sample.categoryId) setCategoryId(sample.categoryId);
    setInterventionId(sample.interventionId);
    setSponsorId(sample.sponsorId);
    setDateFrom(sample.dateFrom);
    setDateTo(sample.dateTo);
    setNumberOfHours(sample.numberOfHours);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      title: title.trim(),
      typeId: typeId!,
      categoryId: categoryId!,
      interventionId,
      sponsorId,

      // Empty stays null: an unrecorded date is a different fact from a date somebody chose.
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,

      // Null rather than 0 when the box is empty — "nobody counted the hours" and "attended
      // none" are different facts, and L&D hours are totalled.
      numberOfHours: hoursValue,

      /*
        The competency-development block is not sent at all, which is what withholding it means:
        the record carries null rather than a value nobody was asked for. An EDIT of a record
        written before 15 Sep 2026 therefore CLEARS what it used to carry — stated here rather
        than discovered, and it is the honest reading: a form that cannot show a field must not
        claim to be preserving it.
      */
    };

    try {
      if (editing) await personnelApi.updateTraining(personnelId, editing.id, request);
      else await personnelApi.addTraining(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      // Every field kebabs straight onto its own input id — `interventionId` becomes
      // `training-intervention-id` — so no `fieldIds` map is needed.
      focusValidationError(failure, { idPrefix: 'training' });
    } finally {
      setSaving(false);
    }
  }

  const options = (catalog: LearningDevelopmentCatalogSlug) =>
    vocabularies[catalog].map((o) => ({ value: o.id, label: o.name }));

  return (
    <Modal
      open
      onClose={onClose}
      // Names the record on an edit, so a reader who opened the wrong row sees it before typing.
      title={editing ? `Edit ${editing.recordNumber}` : 'Add Training/L&D'}
      size="2xl"
      id="training-modal"
      autogenerate={{ onGenerate: generate, disabled: saving || loadingVocabularies }}
      confirm={{
        id: 'training-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="training-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="training-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="training-title"
            label="L&D title"
            required
            description="The programme or intervention, written in full as the form asks."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={title.trim() ? error?.fieldError('title') : 'Enter the title.'}
            disabled={saving}
          />

          <Select
            id="training-sponsor-id"
            label="Conducted / sponsored by"
            /*
              The list starts EMPTY — there is no national vocabulary of sponsors, so it is this
              employer's own and fills from here. That makes the add affordance load-bearing
              rather than a convenience, which is why the description leads with it.
            */
            description={
              canAddVocabulary
                ? "The institution or agency. This list is this employer's own — type a name that is not listed to add it."
                : 'The institution or agency, from the sponsor list maintained under Personnel → Settings.'
            }
            loading={loadingVocabularies}
            allowCustom={canAddVocabulary}
            onCreateOption={canAddVocabulary ? addTo('sponsors', setSponsorId) : undefined}
            value={sponsorId}
            onChange={(next) => setSponsorId(next || null)}
            options={options('sponsors')}
            // The VOCABULARY's order, which is what the server sent.
            sort="none"
            error={error?.fieldError('sponsorId')}
            disabled={saving}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="training-type-id"
            label="L&D type"
            required
            description={
              canAddVocabulary
                ? 'What kind of learning or development this was. Type a name that is not listed to add it.'
                : 'What kind of learning or development this was.'
            }
            loading={loadingVocabularies}
            allowCustom={canAddVocabulary}
            onCreateOption={canAddVocabulary ? addTo('types', setTypeId) : undefined}
            value={typeId}
            onChange={(next) => setTypeId(next || null)}
            options={options('types')}
            sort="none"
            clearable={false}
            error={typeId ? error?.fieldError('typeId') : 'Choose an L&D type.'}
            disabled={saving}
          />

          <Select
            id="training-category-id"
            label="L&D category"
            required
            /*
              The description says outright that this is the CSC column, because the form prints
              different words: a reader looking for "Managerial" needs to know it is asked here
              as Leadership & Management rather than hunting for a second dropdown.
            */
            description="What it was about — the CSC form's Type of LD. Managerial and Supervisory are asked here as Leadership & Management."
            loading={loadingVocabularies}
            allowCustom={canAddVocabulary}
            onCreateOption={canAddVocabulary ? addTo('categories', setCategoryId) : undefined}
            value={categoryId}
            onChange={(next) => setCategoryId(next || null)}
            options={options('categories')}
            sort="none"
            clearable={false}
            error={categoryId ? error?.fieldError('categoryId') : 'Choose an L&D category.'}
            disabled={saving}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="training-intervention-id"
            label="Development intervention"
            /*
              Optional and always drawn, never hidden behind the L&D type it shares a word with —
              Formal Classroom Training is plainly a training, and hiding this on a training row
              would make an on-the-job one unrecordable. The description says so, because the
              naming invites the opposite reading.
            */
            description="How it was delivered. Asked of every record, not only of a developmental activity."
            loading={loadingVocabularies}
            allowCustom={canAddVocabulary}
            onCreateOption={canAddVocabulary ? addTo('interventions', setInterventionId) : undefined}
            value={interventionId}
            onChange={(next) => setInterventionId(next || null)}
            options={options('interventions')}
            sort="none"
            error={error?.fieldError('interventionId')}
            disabled={saving}
          />

          <TextField
            id="training-number-of-hours"
            label="Number of hours"
            type="number"
            min={0}
            step="1"
            description="Total hours. Leave blank if nobody recorded them — that is not the same as none."
            value={numberOfHours}
            onChange={(e) => setNumberOfHours(e.target.value)}
            error={
              !hoursIsNumber
                ? 'Enter a whole number of hours.'
                : !hoursNotNegative
                  ? 'Enter a number of hours of 0 or more.'
                  : error?.fieldError('numberOfHours')
            }
            disabled={saving}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="training-date-from"
            label="From"
            max={today}
            description="Optional — plenty of older records carry only a title and a sponsor."
            value={dateFrom}
            onChange={setDateFrom}
            error={error?.fieldError('dateFrom')}
            disabled={saving}
          />

          <DateField
            id="training-date-to"
            label="To"
            max={today}
            value={dateTo}
            onChange={setDateTo}
            error={
              endsBeforeStart
                ? 'Choose a date on or after the day it began.'
                : error?.fieldError('dateTo')
            }
            disabled={saving}
          />
        </div>

        {/*
          The competency-development block used to sit here — purpose, the competencies
          addressed, and the levels held and targeted. Withheld on a product call of
          15 Sep 2026 and left OUT rather than disabled: a control a reader can never operate is
          noise, which is the hide-don't-disable rule.

          Nothing else moved. The columns, the DTO and the endpoint all still carry the four
          fields, so restoring the block is an edit to this file — see `PersonnelTraining`.
        */}
      </div>
    </Modal>
  );
}
